import i18n from "@/i18n";
import {i18nTextApi} from "@/boxhub-api";
import {Language} from "@/api/data-contracts";
import {LANGUAGE_TO_LNG} from "@/app/locale/languageCodes";

/**
 * Segments that must never be written as an object key while walking a server-provided dot path.
 * Without this guard, a key such as `"__proto__.polluted"` or `"a.constructor.prototype.polluted"`
 * would traverse into `Object.prototype` via a plain `{}` node and pollute it for the entire
 * browser session (prototype-pollution guard) — any key path containing one of these segments is
 * dropped entirely rather than partially written.
 */
const UNSAFE_SEGMENTS = new Set(["__proto__", "constructor", "prototype"]);

/**
 * Converts a flat, dot-path keyed map (e.g. `{ "common.header.action.logout": "Abmelden" }`) into
 * a nested object tree suitable for `i18n.addResourceBundle`. Only dots are treated as path
 * separators — underscores inside a leaf key segment (e.g. `common.enum.legalForm.GmbH_Co_KG`)
 * remain part of that segment.
 */
export function unflatten(flat: Record<string, string>): Record<string, unknown> {
    const root: Record<string, unknown> = {};
    for (const [flatKey, value] of Object.entries(flat)) {
        const segments = flatKey.split(".");
        if (segments.some((segment) => UNSAFE_SEGMENTS.has(segment))) {
            continue;
        }
        const lastIndex = segments.length - 1;
        let node = root;
        segments.forEach((segment, index) => {
            if (index === lastIndex) {
                node[segment] = value;
                return;
            }
            const existing = node[segment];
            const child = typeof existing === "object" && existing !== null ? existing : {};
            node[segment] = child;
            node = child as Record<string, unknown>;
        });
    }
    return root;
}

/**
 * Fetches the raw, flat (dot-path keyed) text bundle for the given language from the backend.
 * Never throws — a failed request (network error, 4xx/5xx) is logged and reported as `null`.
 *
 * `null` (request failed) and `{}` (server legitimately has no maintained texts for this language)
 * are deliberately distinguished: only the latter is a final answer that may be cached for the rest
 * of the page's lifetime. Treating a failed request as an empty bundle would freeze the whole UI
 * into showing raw translation keys until the user reloads by hand, because the failed language
 * would never be fetched again (see `fetchedLanguages`).
 */
export async function fetchBundle(language: Language): Promise<Record<string, string> | null> {
    try {
        const {data} = await i18nTextApi.getTexts(language);
        return data;
    } catch (error: unknown) {
        console.error(`Failed to fetch language bundle for ${language}`, error);
        return null;
    }
}

/**
 * Language codes whose backend text bundle has been fetched **successfully** during this page's
 * lifetime — used by `loadLanguageBundle` to avoid re-fetching a bundle it already retrieved.
 * This is intentionally a runtime-fetch marker and NOT the same thing as
 * `i18n.hasResourceBundle(lng, "translation")`: a language that legitimately has no maintained
 * texts yields an empty bundle and registers no usable resource, so `hasResourceBundle` would stay
 * `false` and that language would be re-fetched on every call.
 *
 * A FAILED fetch is deliberately not recorded here, so the next `loadLanguageBundle` /
 * `changeAppLanguage` call retries. Recording failures too would mean a single hiccup during
 * bootstrap (backend restart, dropped connection) leaves the entire UI showing raw translation
 * keys until the user reloads the page by hand — with no retry and no visible error.
 */
const fetchedLanguages = new Set<string>();

/**
 * Test-only reset of the fetched-languages cache — call in `afterEach` when a test exercises the
 * double-fetch-avoidance behavior across multiple `loadLanguageBundle` calls for the same
 * language, so state does not leak into the next test.
 */
export function resetFetchedLanguagesForTests(): void {
    fetchedLanguages.clear();
}

/**
 * Loads the runtime UI text bundle for the given language from the backend and registers it with
 * i18next under the matching language code. Never throws — `fetchBundle` already swallows its own
 * errors and reports them as `null`, so the app bootstrap spinner never hangs waiting on this call.
 *
 * Skips the network request entirely when this language's bundle was already fetched successfully
 * during this page's lifetime — this is the "avoid double fetches" reuse the bootstrap relies on
 * (see `fetchedLanguages`). A failed fetch registers nothing and is retried on the next call.
 */
export async function loadLanguageBundle(language: Language): Promise<void> {
    const lng = LANGUAGE_TO_LNG[language];
    if (fetchedLanguages.has(lng)) {
        return;
    }
    const data = await fetchBundle(language);
    if (data === null) {
        return;
    }
    fetchedLanguages.add(lng);
    const bundle = unflatten(data);
    i18n.addResourceBundle(lng, "translation", bundle, true, true);
}

/**
 * Re-fetches the runtime UI text bundle for `language` and replaces the registered one, bypassing
 * the `fetchedLanguages` short-circuit — the counterpart of `loadLanguageBundle` for the case where
 * the stored texts are known to have changed under a running session (the operator language-file
 * import).
 *
 * The old bundle is removed before the new one is registered, so a key deleted by the import
 * disappears instead of surviving the deep merge. When the refreshed language is the one currently
 * displayed, i18next is re-pointed at it afterwards: `addResourceBundle` alone emits only a store
 * event, which `react-i18next` components do not subscribe to by default, so the already rendered
 * texts would keep showing the previous values.
 *
 * Never throws — a failed re-fetch leaves the currently registered bundle untouched (better a
 * slightly stale text than an all-keys UI) and is reported to the caller as `false`.
 */
export async function refreshLanguageBundle(language: Language): Promise<boolean> {
    const lng = LANGUAGE_TO_LNG[language];
    const data = await fetchBundle(language);
    if (data === null) {
        return false;
    }
    i18n.removeResourceBundle(lng, "translation");
    i18n.addResourceBundle(lng, "translation", unflatten(data), true, true);
    fetchedLanguages.add(lng);
    if (i18n.language === lng) {
        try {
            await i18n.changeLanguage(lng);
        } catch (error: unknown) {
            console.error(`Failed to re-apply refreshed language bundle for ${language}`, error);
        }
    }
    return true;
}

/**
 * Single entry point for switching the app's active language — both at bootstrap and on an
 * explicit language change. Always loads the runtime text bundle for the target language first,
 * then switches i18next to it. Never throws: `loadLanguageBundle` already swallows its own errors,
 * and the `i18n.changeLanguage` call below is likewise caught and logged so callers (e.g. the
 * bootstrap `finally` block gating the loading spinner) can always safely `await` this function.
 */
export async function changeAppLanguage(language: Language): Promise<void> {
    await loadLanguageBundle(language);
    try {
        await i18n.changeLanguage(LANGUAGE_TO_LNG[language]);
    } catch (error: unknown) {
        console.error(`Failed to change app language to ${language}`, error);
    }
}
